import React, { useState } from 'react';
import { AdminLayout } from '../../layout/AdminLayout';
import { Database, Activity, Calendar, Clock, DollarSign, Eye } from 'lucide-react';
import { useAsync } from 'react-use';
import { useApiClient } from '../../base/hooks/useApiClient';
import { Table, Column } from '../../base/Table';
import * as flagIconComponents from 'country-flag-icons/react/3x2';
import { useNavigate } from 'react-router-dom';

interface SessionRun {
    id: number;
    startedAt: string;
    duration: number | null;
    languageDurations: Record<string, number>;
    cost: number | null;
    user: {
        id: number;
        username: string;
        email: string;
    };
    sessionName: string;
    sessionUuid: string;
    originalLanguage: string;
    originalLanguageName: string;
    originalLanguageCountry: string;
}

interface SessionStats {
    totalSessionsMonth: number;
    totalSessionsWeek: number;
    activeSessions: number;
}

interface SessionRunsResponse {
    runs: SessionRun[];
    pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
    };
}

export function AdminSessionsPage() {
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [sortColumn, setSortColumn] = useState('startedAt');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const limit = 15;

    const { value: stats, loading: statsLoading } = useAsync(async () => {
        const response = await apiClient.get('admin/sessions/stats');
        if (!response.ok) {
            console.error('Error fetching session stats');
            return null;
        }
        return await response.json() as SessionStats;
    });

    const { value: runsData, loading: runsLoading } = useAsync(async () => {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sortBy: sortColumn,
            sortOrder: sortDirection,
        });
        
        const response = await apiClient.get(`admin/sessions/runs?${params}`);
        if (!response.ok) {
            console.error('Error fetching session runs');
            return null;
        }
        return await response.json() as SessionRunsResponse;
    }, [page, sortColumn, sortDirection]);

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        setPage(1); // Reset to first page when sorting changes
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const formatDuration = (milliseconds: number | null) => {
        if (milliseconds === null) return 'In Progress';
        const seconds = milliseconds / 1000;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        
        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    };

    const formatCost = (cost: number | null) => {
        if (cost === null) return '-';
        return `${Number(cost).toFixed(2)}`;
    };

    const columns: Column<SessionRun>[] = [
        {
            key: 'user',
            label: 'User',
            render: (run) => (
                <div>
                    <div className="text-sm font-medium text-gray-900">
                        {run.user.username}
                    </div>
                    <div className="text-xs text-gray-500">
                        {run.user.email}
                    </div>
                </div>
            ),
        },
        {
            key: 'sessionName',
            label: 'Session Name',
            render: (run) => (
                <div className="text-sm text-gray-900">{run.sessionName}</div>
            ),
        },
        {
            key: 'duration',
            label: 'Duration',
            columnType: 'number',
            sortable: false,
            render: (run) => (
                <div className="flex items-center text-sm text-gray-600">
                    {run.duration === null && '-'}
                    {run.duration !== null && (
                        <>
                            <Clock className="h-4 w-4 mr-1 text-gray-400" />
                            {formatDuration(run.duration)}
                        </>
                    )}
                </div>
            ),
        },
        {
            key: 'languageDurations',
            label: 'Language Durations',
            sortable: false,
            render: (run) => {
                const entries = Object.entries(run.languageDurations);
                if (entries.length === 0) return <div className="text-sm text-gray-400">-</div>;

                return (
                    <div className="flex flex-col gap-1">
                        {entries.map(([langCode, ms]) => {
                            const country = langCode.split('-').pop() || '';
                            const Flag = flagIconComponents[country as keyof typeof flagIconComponents] as flagIconComponents.FlagComponent;
                            return (
                                <div key={langCode} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                                    {Flag && (
                                        <div className="w-4 h-2.5 overflow-hidden rounded shadow-sm flex-shrink-0">
                                            <Flag />
                                        </div>
                                    )}
                                    <span>{formatDuration(ms)}</span>
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            key: 'cost',
            label: 'Cost',
            columnType: 'number',
            render: (run) => (
                <div className="flex items-center text-sm text-gray-600">
                    {run.cost === null && '-'}
                    {run.cost !== null && (
                        <>
                            <DollarSign className="h-4 w-4 mr-1 text-gray-400" />
                            {formatCost(run.cost)}
                        </>
                    )}
                </div>
            ),
        },
        {
            key: 'originalLanguage',
            label: 'Original Lang',
            sortable: false,
            render: (run) => {
                const Flag = flagIconComponents[run.originalLanguageCountry as keyof typeof flagIconComponents] as flagIconComponents.FlagComponent;
                return (
                    <span className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {Flag && (
                            <div className="w-5 h-3 overflow-hidden rounded shadow-sm flex-shrink-0">
                                <Flag />
                            </div>
                        )}
                        <span>{run.originalLanguageName}</span>
                    </span>
                );
            },
        },
        {
            key: 'startedAt',
            label: 'Started At',
            columnType: 'datetime',
            render: (run) => (
                <div className="text-sm text-gray-600">
                    {new Intl.DateTimeFormat('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }).format(new Date(run.startedAt))}
                </div>
            ),
        },
        {
            key: 'actions',
            label: 'Actions',
            sortable: false,
            render: (run) => (
                <button
                    onClick={() => navigate(`/panel/sessions/${run.sessionUuid}/runs/${run.id}`)}
                    className="flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors text-sm"
                >
                    <Eye className="h-4 w-4 mr-1" />
                    Details
                </button>
            ),
        },
    ];

    return (
        <AdminLayout>
            <div className="max-w-7xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Sessions Management</h1>
                    <p className="mt-2 text-gray-600">Monitor and manage all translation sessions</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Sessions (Last Month)</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {statsLoading ? '-' : stats?.totalSessionsMonth || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Calendar className="h-6 w-6 text-purple-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Sessions (Last Week)</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {statsLoading ? '-' : stats?.totalSessionsWeek || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Database className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Active Sessions</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {statsLoading ? '-' : stats?.activeSessions || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <Activity className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Session Runs Table */}
                <Table
                    title="All Session Runs"
                    data={runsData?.runs}
                    columns={columns}
                    loading={runsLoading}
                    emptyMessage="No session runs found"
                    defaultSortKey="startedAt"
                    defaultSortDirection="desc"
                    getRowKey={(run) => run.id}
                    pagination={{
                        itemsPerPage: limit,
                        currentPage: page,
                        totalItems: runsData?.pagination.totalCount || 0,
                        onPageChange: handlePageChange,
                    }}
                    sort={{
                        sortColumn,
                        sortDirection,
                        onSortChange: handleSortChange,
                    }}
                />
            </div>
        </AdminLayout>
    );
}
